import { type KeyboardEvent, useRef, useState } from "react";

export interface ProSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectKeyboardProps {
  options: ProSelectOption[];
  value: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (option: ProSelectOption) => void;
}

export function useProSelectKeyboard({
  options,
  value,
  open,
  onOpen,
  onClose,
  onSelect,
}: SelectKeyboardProps) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef({ text: "", time: 0 });
  const enabledIndexes = options.flatMap((option, index) => (option.disabled ? [] : [index]));
  const selectedIndex = options.findIndex((option) => option.value === value && !option.disabled);
  const initialIndex = (fromEnd = false) =>
    selectedIndex >= 0 ? selectedIndex : (enabledIndexes.at(fromEnd ? -1 : 0) ?? -1);

  const openOptions = (fromEnd = false) => {
    setActiveIndex(initialIndex(fromEnd));
    searchRef.current = { text: "", time: 0 };
    onOpen();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    } else if (event.key === "Tab") {
      onClose();
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!open) openOptions();
      else if (options[activeIndex]) onSelect(options[activeIndex]);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const backwards = event.key === "ArrowUp";
      if (!open) openOptions(backwards);
      else {
        const current = enabledIndexes.indexOf(activeIndex);
        const next =
          current < 0
            ? backwards
              ? enabledIndexes.length - 1
              : 0
            : (current + (backwards ? -1 : 1) + enabledIndexes.length) % enabledIndexes.length;
        setActiveIndex(enabledIndexes[next] ?? -1);
      }
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      if (!open) openOptions();
      setActiveIndex(enabledIndexes.at(event.key === "End" ? -1 : 0) ?? -1);
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const previous = open && now - searchRef.current.time < 700 ? searchRef.current.text : "";
      const character = event.key.toLocaleLowerCase();
      const text = previous === character ? character : previous + character;
      searchRef.current = { text, time: now };
      const currentIndex = open ? activeIndex : initialIndex();
      const start = previous && text !== character ? currentIndex : currentIndex + 1;
      const indexes = options.map((_, index) => (Math.max(start, 0) + index) % options.length);
      const match = indexes.find((index) => {
        const option = options[index];
        return option && !option.disabled && option.label.toLocaleLowerCase().startsWith(text);
      });
      onOpen();
      setActiveIndex(match ?? currentIndex);
    }
  };

  return { activeIndex, openOptions, onKeyDown };
}
